// apiService.ts - Centralized API service (UPDATED: authorized moved to region-level)
import { Organization, OrganizationFormData, Region, CompanyAuthorizationResult } from './types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

export class ApiService {
  constructor(private getAccessToken: () => Promise<string>) {}

  // Region API calls
  async loadRegions(): Promise<{ success: boolean; data?: Region[]; message?: string }> {
    try {
      console.log('🗺️ Loading regions...');
      const accessToken = await this.getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/regions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🗺️ Regions response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('🗺️ Regions response data:', data);
        
        if (data.success) {
          console.log('✅ Regions loaded successfully:', data.data.length);
          return { success: true, data: data.data };
        } else {
          console.error('❌ Regions API returned success:false:', data);
          return { success: false, message: data.message || 'Failed to load regions' };
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Regions HTTP error:', response.status, errorText);
        
        let userMessage;
        if (response.status === 401) {
          userMessage = 'Authentication failed. Please sign in again.';
        } else if (response.status === 403) {
          userMessage = 'Access denied. Admin privileges required.';
        } else {
          userMessage = `Failed to load regions (${response.status}): ${errorText}`;
        }
        
        return { success: false, message: userMessage };
      }
    } catch (error) {
      console.error('💥 Network error loading regions:', error);
      
      let errorMessage = 'Failed to load regions';
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = 'Network error - unable to connect to server';
      } else if (error instanceof Error) {
        errorMessage = `Error: ${error.message}`;
      }
      
      return { success: false, message: errorMessage };
    }
  }

  // Organization API calls
  async loadOrganizations(): Promise<{ success: boolean; data?: Organization[]; message?: string }> {
    try {
      console.log('🏢 Loading organizations...');
      const accessToken = await this.getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/organizations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🏢 Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('🏢 Response data:', data);
        
        if (data.success) {
          console.log('✅ Organizations loaded successfully:', data.data.length);
          return { 
            success: true, 
            data: data.data,
            message: `Loaded ${data.data.length} organizations successfully`
          };
        } else {
          console.error('❌ API returned success:false:', data);
          return { success: false, message: data.message || 'Failed to load organizations' };
        }
      } else {
        const errorText = await response.text();
        console.error('❌ HTTP error:', response.status, errorText);
        
        let userMessage;
        if (response.status === 401) {
          userMessage = 'Authentication failed. Please sign in again.';
        } else if (response.status === 403) {
          userMessage = 'Access denied. Admin privileges required.';
        } else if (response.status === 404) {
          userMessage = 'Admin endpoint not found. Please check deployment.';
        } else {
          userMessage = `Server error (${response.status}): ${errorText}`;
        }
        
        return { success: false, message: userMessage };
      }
    } catch (error) {
      console.error('💥 Network error loading organizations:', error);
      
      let errorMessage = 'Failed to load organizations';
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = 'Network error - unable to connect to server';
      } else if (error instanceof Error) {
        errorMessage = `Error: ${error.message}`;
      }
      
      return { success: false, message: errorMessage };
    }
  }

  async createOrganization(formData: OrganizationFormData): Promise<{ success: boolean; message?: string }> {
    try {
      const accessToken = await this.getAccessToken();
      const url = `${API_BASE_URL}/orgcreate`;
      
      console.log('🏢 Creating organization:', url);
      console.log('🏢 Form data:', formData);

      // UPDATED: Build payload without top-level authorized; authorized is per-region
      const payload = {
        companyName: formData.companyName,
        // authorized is NOT sent at company level anymore
        blacklisted: formData.blacklisted,
        description: formData.description,
        contactNumber: formData.contactNumber,
        url: formData.url,
        contactEmail: formData.contactEmail,
        regions: formData.regions.map(r => ({
          regionId: r.regionId,
          fileId: r.fileId || null,
          authorized: r.authorized !== undefined ? r.authorized : true, // NEW: per-region
          isActive: r.isActive,
          description: r.description || null
        }))
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('🏢 Create response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('🏢 Create response data:', data);
        
        if (data.success) {
          return {
            success: true,
            message: `Organization "${formData.companyName}" created successfully`
          };
        } else {
          return { success: false, message: data.message || 'Creation failed' };
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Create error:', response.status, errorText);
        
        let userMessage;
        if (response.status === 400) {
          userMessage = 'Invalid data provided. Please check all fields.';
        } else if (response.status === 409) {
          userMessage = 'An organization with this name already exists.';
        } else {
          userMessage = `Creation failed (${response.status}): ${errorText}`;
        }
        
        return { success: false, message: userMessage };
      }
    } catch (error) {
      console.error('💥 Create error:', error);
      return {
        success: false,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  async updateOrganization(organizationId: number, formData: OrganizationFormData): Promise<{ success: boolean; message?: string }> {
    try {
      const accessToken = await this.getAccessToken();
      const url = `${API_BASE_URL}/orgupdate/${organizationId}`;
      
      console.log('🏢 Updating organization:', url);
      console.log('🏢 Form data:', formData);

      // UPDATED: Build payload without top-level authorized; authorized is per-region
      const payload = {
        companyName: formData.companyName,
        // authorized is NOT sent at company level anymore
        blacklisted: formData.blacklisted,
        description: formData.description,
        contactNumber: formData.contactNumber,
        url: formData.url,
        contactEmail: formData.contactEmail,
        regions: formData.regions.map(r => ({
          regionId: r.regionId,
          fileId: r.fileId || null,
          authorized: r.authorized !== undefined ? r.authorized : true, // NEW: per-region
          isActive: r.isActive,
          description: r.description || null
        }))
      };
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('🏢 Update response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('🏢 Update response data:', data);
        
        if (data.success) {
          return {
            success: true,
            message: `Organization "${formData.companyName}" updated successfully`
          };
        } else {
          return { success: false, message: data.message || 'Update failed' };
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Update error:', response.status, errorText);
        
        let userMessage;
        if (response.status === 400) {
          userMessage = 'Invalid data provided. Please check all fields.';
        } else if (response.status === 404) {
          userMessage = 'Organization not found.';
        } else if (response.status === 409) {
          userMessage = 'An organization with this name already exists.';
        } else {
          userMessage = `Update failed (${response.status}): ${errorText}`;
        }
        
        return { success: false, message: userMessage };
      }
    } catch (error) {
      console.error('💥 Update error:', error);
      return {
        success: false,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  async deleteOrganization(organization: Organization): Promise<{ success: boolean; message?: string }> {
    try {
      const accessToken = await this.getAccessToken();
      const url = `${API_BASE_URL}/orgdelete/${organization.companyId}`;
      
      console.log('🏢 Deleting organization:', organization.companyId);
      console.log('🏢 Delete URL:', url);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      console.log('🏢 Delete response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('🏢 Delete response data:', data);
        
        if (data.success) {
          return {
            success: true,
            message: `Organization "${organization.companyName}" deleted successfully`
          };
        } else {
          return { success: false, message: data.message || 'Failed to delete organization' };
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Delete error:', response.status, errorText);
        
        let userMessage;
        if (response.status === 404) {
          userMessage = 'Organization not found.';
        } else if (response.status === 409) {
          userMessage = 'Cannot delete organization - it is referenced in existing requests.';
        } else {
          userMessage = `Delete failed (${response.status}): ${errorText}`;
        }
        
        return { success: false, message: userMessage };
      }
    } catch (error) {
      console.error('💥 Delete error:', error);
      return {
        success: false,
        message: `Error deleting organization: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  // NEW: Check if a company is authorized for a specific region
  async isCompanyAuthorized(companyId: number, regionId: number): Promise<CompanyAuthorizationResult> {
    try {
      const accessToken = await this.getAccessToken();
      const url = `${API_BASE_URL}/companies/${companyId}/authorized/${regionId}`;
      
      console.log('🔐 Checking authorization:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return {
          isAuthorized: data.isAuthorized || false,
          reason: data.reason,
          details: data.details
        };
      } else {
        console.error('❌ Authorization check failed:', response.status);
        return { isAuthorized: false, reason: 'Failed to check authorization' };
      }
    } catch (error) {
      console.error('💥 Authorization check error:', error);
      return { isAuthorized: false, reason: 'Network error checking authorization' };
    }
  }
}